import { Utils, Logger, PreloaderInfo } from "neo-common";
import { AnimationFramePool } from "../AnimationFrame/Pool";
import { Scaling } from "../Utils/Scaling";
import { AppContext } from "../../Common/AppContext";
import { ModuleManager } from "../Managers/ModuleManager";
import { Communication } from "neo-games";
import { GameManager } from "../Managers/GameManager";
import { Focus } from "../Utils/Focus";
import { Languages } from "../Utils/Languages";
import { PageVisibilityHandler } from "../CustomComponents/PageVisibilityHandler";
import { DataManager } from "../Managers/DataManager";
import { HtmlController } from "../HTMLComponents/HtmlController";

/**
 * StageController creates stage for webgl rendering (with fallback to canvas).
 * Also handle redrawing stage based on tick with requestAnimationFrame.
 * @class StageController
 */
export class StageController {
    static Renderer: any;
    static Stage: PIXI.Container;
    static UpdatedTextures: Object = {};
    static Width: number;
    static Height: number;
    static FPS: number = 60;
    static baseStageContainer: PIXI.Container;
    static freespinsStageContainer: PIXI.Container;
    static bonusStageContainer: PIXI.Container;
    static baseStage: boolean = false;
    static bonusStage: boolean = false;

    private static _noWebGL: boolean = false;
    private static _pauseAnimations = false;
    private static _contextLost = false;

    public static pauseAnimations() {
        if (DEBUG) {
            StageController._pauseAnimations = true;
            Logger.debugGroup("StageController", "Animations was paused");
        }
    }

    public static resumeAnimations() {
        if (DEBUG) {
            if (StageController._pauseAnimations) {
                StageController._pauseAnimations = false;
                StageController.redrawStage();

                Logger.debugGroup("StageController", "Animations was resumed");
            }
        }
    }

    public static get textureManager(): PIXI.TextureManager {
        return StageController.Renderer.textureManager;
    }

    public static updateTexture(texture: PIXI.BaseTexture | PIXI.Texture): WebGLTexture {
        return this._noWebGL ? null : StageController.Renderer.textureManager.updateTexture(texture);
    }

    public static getTextureByResourceId(id: string): PIXI.Texture {
        let texture: PIXI.Texture = PIXI.utils.TextureCache[id];
        let resourcePath: string = "";

        if (Utils.isNullOrUndefined(texture)) {
            resourcePath = PreloaderInfo.instance.getResourcePathById(id);

            if (resourcePath) {
                texture = PIXI.utils.TextureCache[resourcePath];

                if (Utils.isNullOrUndefined(texture)) {
                    Logger.errorGroup(
                        "StageController",
                        `Could not find texture by id '${id}' (resourcePath: ${resourcePath})`,
                    );
                }
            } else {
                Logger.errorGroup("StageController", `Could not find resource path for id '${id}' !`);
            }
        }

        return texture;
    }

    private static handleLostWebGLContext(): void {
        if (StageController._contextLost) {
            return;
        }
        DataManager.Autoplay = false;
        PageVisibilityHandler.Enabled = false;
        PageVisibilityHandler.pauseAll();
        HtmlController.dispatchActionAllowed = false;

        let modules = Communication.Game.GameModules;
        for (let key in modules) {
            if (modules.hasOwnProperty(key)) {
                ModuleManager.disableChannelModule(modules[key]);
            }
        }
        GameManager.gameClient.sendPlayerLogError("ERROR - WebGL");
        AppContext.current.showDialog(Languages.getRid("ERROR_WEBGL"), false, "", "", "", { special: true });
        StageController._contextLost = true;
    }

    /**
     * Create stage, detect renderer, set size and color of stage, start rendering.
     * @method createStage
     * @param width number
     * @param height number
     * @param color number (background color of canvas)
     * @param canvasId string
     * @return void
     */
    public static createStage(width: number, height: number, color: number, canvasId: string): void {
        StageController.Width = width;
        StageController.Height = height;

        // get canvas from html document
        const canvas: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById(canvasId);
        const loading: HTMLCanvasElement = <HTMLCanvasElement>document.getElementById("loading");

        // create rendered
        const rendererOptions: PIXI.WebGLRendererOptions = { view: canvas, roundPixels: false };
        (rendererOptions as any).backgroundColor = color;
        this.Renderer = PIXI.autoDetectRenderer(width, height, rendererOptions, this._noWebGL);

        if (StageController.Renderer instanceof PIXI.CanvasRenderer) {
            StageController._noWebGL = true;
        }

        PIXI.settings.GC_MODE = PIXI.GC_MODES.AUTO;

        // append canvas to html document
        document.body.appendChild(this.Renderer.view);

        // create first conatiner to renderer
        this.Stage = new PIXI.Container();

        Scaling.scale();
        Focus.focus();
        PageVisibilityHandler.handle();

        loading.style.display = "block";
        canvas.style.display = "none";
        canvas.addEventListener("webglcontextlost", StageController.handleLostWebGLContext);

        window["__PIXI_STAGE__"] = this.Stage;
        window["__PIXI_RENDERER__"] = this.Renderer;
        this.redrawStage();
    }

    /**
     * Method addChild append new containers to stage on top layer.
     * @method addChild
     * @param element PIXI.DisplayObject
     * @return void
     */
    public static addChild(element: PIXI.DisplayObject): void {
        this.Stage.addChild(element);
    }

    /**
     * Redraw stage based on requestAnimationFrame.
     * @method redrawStage
     * @return void
     */
    private static redrawStage(): void {
        if (StageController._pauseAnimations) {
            return;
        }

        AnimationFramePool.draw();

        if (
            StageController.baseStage &&
            StageController.baseStageContainer != null &&
            StageController.freespinsStageContainer != null
        ) {
            StageController.Renderer.render(StageController.Stage);
        } else if (StageController.baseStage && StageController.baseStageContainer != null) {
            StageController.Renderer.render(StageController.baseStageContainer);
        } else if (StageController.bonusStage && StageController.bonusStageContainer != null) {
            StageController.Renderer.render(StageController.bonusStageContainer);
        } else {
            StageController.Renderer.render(StageController.Stage);
        }

        requestAnimationFrame(StageController.redrawStage);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/StageController.ts
 **/